import {
  LitElement,
  html,
  css,
  unsafeHTML,
  ref,
  createRef,
  utils,
  paths,
  t,
  styles,
} from '@hh/global.js'

class HHSlider extends LitElement {
  static properties = {
    active: {
      reflect: true,
    },
    breakpoint: {
      type: Number,
    },
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
  }

  $container = createRef()
  $next = createRef()
  $prev = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 1

    this.updateUI = this.updateUI.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this._total = this.items.length

    this.observer = new ResizeObserver((...args) => {
      this.activate(...args)
    })

    setTimeout(() => {
      this.$container?.value?.addEventListener('scroll', this.updateUI)
      if (this.breakpoint) {
        this.observer.observe(document.body)
      }
    }, 0)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.$container?.value?.removeEventListener('scroll', this.updateUI)
    this.observer.disconnect()
  }

  activate([first, ...all]) {
    const { contentRect: { width = 0 } = {} } = first || {}
    if (width < this.breakpoint && !this.active) {
      this.active = true
    } else if (width >= this.breakpoint && this.active) {
      this.active = false
    }
  }

  updateUI() {
    if (!this._total) {
      return
    }

    this._current =
      Math.round(
        this.$container?.value?.scrollLeft / this.items[0].clientWidth
      ) + 1
  }

  move(event) {
    event.preventDefault()
    const slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.$container?.value?.scrollLeft + this.items[0].clientWidth
        : this.$container?.value?.scrollLeft - this.items[0].clientWidth
    this.$container?.value?.scrollTo({
      left: slideScrollPosition,
    })
  }

  render() {
    return html`
      <div class="container" ${ref(this.$container)} role="list">
        <slot name="item"></slot>
      </div>
      <div class="buttons">
        <div class="counter">
          <span class="counter--current">${this._current}</span>
          <span aria-hidden="true"> / </span>
          <span class="visually-hidden">${t('accessibility.of', 'of')}</span>
          <span class="counter--total">${this._total ? this._total : ''}</span>
        </div>
        <button
          ${ref(this.$prev)}
          @click="${this.move}"
          type="button"
          class="button button--prev"
          name="previous"
          aria-label="${t('accessibility.previous_slide', 'Previous')}"
          ?disabled="${this._current === 1}"
        >
          ${unsafeHTML(paths.caret)}
        </button>
        <button
          ${ref(this.$next)}
          @click="${this.move}"
          type="button"
          class="button button--next"
          name="next"
          aria-label="${t('accessibility.next_slide', 'Next')}"
          ?disabled="${this.__current === this._total}"
        >
          ${unsafeHTML(paths.caret)}
        </button>
      </div>
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host([active='true']) {
        position: relative;
      }

      .container {
        width: var(--hh-container-width, 100%);
        display: var(--hh-container-flex);
        flex-wrap: var(--hh-container-flex-wrap);
        margin: var(--hh-container-margin);
      }

      :host([active='true']) .container {
        display: flex;
        flex-wrap: nowrap;
        position: relative;
        background: var(--hh-background);
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        scroll-padding-left: 1rem;
        -webkit-overflow-scrolling: touch;
      }

      :host([active='true']) ::slotted(*) {
        width: var(--hh-cell-width) !important;
      }

      .buttons {
        display: none;
        align-items: center;
        position: absolute;
        z-index: 2;
        right: 0;
        bottom: 0;
        border: var(--hh-button-border);
        background: var(--hh-button-background);
      }

      :host([active='true']) .buttons {
        display: flex;
      }

      .counter {
        margin: 0 2rem;
      }

      .button {
        width: var(--hh-button-width, 44px);
        height: var(--hh-button-height, 44px);
        cursor: pointer;
        color: var(--hh-color);
        border: none;
        background: var(--hh-button-background);
      }

      .button svg {
        height: 0.6rem;
      }

      .button--prev {
        margin-left: -0.2rem;
        transform: rotate(90deg);
      }

      .button--next {
        margin-left: -0.2rem;
        transform: rotate(-90deg);
      }
    `,
  ]
}

customElements.define('hh-slider', HHSlider)
